import { useState, useEffect } from 'react'
import { getUnitPreference, setUnitPreference } from '../utils/userPreferences'
import { useTheme } from '../contexts/ThemeContext'
import type { ThemePreference } from '../types/theme'
import { UnitPreference } from '../types/vitalSigns'
import './SettingsPreferences.css'

export function SettingsPreferences() {
  const [unitPreference, setUnitPreferenceState] = useState<UnitPreference>(() => getUnitPreference())
  const { theme, setTheme } = useTheme()

  useEffect(() => {
    setUnitPreferenceState(getUnitPreference())
  }, [])

  const handleUnitPreferenceChange = (preference: UnitPreference) => {
    setUnitPreference(preference)
    setUnitPreferenceState(preference)
    alert('Unit preference saved. Changes will apply to all vital signs displays.')
  }

  const handleThemeChange = (nextTheme: ThemePreference) => {
    setTheme(nextTheme)
    alert('Theme preference saved. The new theme is now applied across the app.')
  }

  return (
    <div className="settings-preferences">
      <div className="settings-section">
        <h2>Display Preferences</h2>
        <div className="preferences-group">
          <div className="preference-item">
            <label htmlFor="unit-preference">Unit System</label>
            <p className="preference-description">
              Choose between Imperial (feet, inches, pounds, Fahrenheit) or Metric (cm, kg, Celsius) units for displaying vital signs.
            </p>
            <div className="preference-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="unit-preference"
                  value="metric"
                  checked={unitPreference === 'metric'}
                  onChange={() => handleUnitPreferenceChange('metric')}
                />
                <span>Metric (cm, kg, °C)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="unit-preference"
                  value="imperial"
                  checked={unitPreference === 'imperial'}
                  onChange={() => handleUnitPreferenceChange('imperial')}
                />
                <span>Imperial (feet/inches, pounds, °F)</span>
              </label>
            </div>
          </div>

          <div className="preference-item">
            <label htmlFor="theme-preference">Theme</label>
            <p className="preference-description">
              Choose how Clinicly looks. Standard Mode matches the current design, Dark Mode is optimized for low-light environments, and Accessibility Mode
              increases contrast and text size for improved readability.
            </p>
            <div className="preference-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="theme-preference"
                  value="standard"
                  checked={theme === 'standard'}
                  onChange={() => handleThemeChange('standard')}
                />
                <span>Standard Mode</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="theme-preference"
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={() => handleThemeChange('dark')}
                />
                <span>Dark Mode</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="theme-preference"
                  value="accessibility"
                  checked={theme === 'accessibility'}
                  onChange={() => handleThemeChange('accessibility')}
                />
                <span>Accessibility Mode</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
